import BaseModel from '../base';
import TaskQuestion from '../question/index';
import TaskSettings from './settings/index';
import TournamentStageGroup from '../tournament/stage/group/index';
import Journal from '../journal/index';
import Group from '../group';

import constants from './constants';
import glossary from './glossary';
import functions from './functions';

import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import { dateFnsFormat } from '@/helpers/dateFns';

const { readableShortDateWithMonth } = dateFnsFormat;

export default class Task extends BaseModel {
  constructor(task = {}, url) {
    super(task, url);

    this.name = task.name || '';
    this.type = Number(task.type) || this.constructor.constants.TASK_TYPES.AUTOMATED_TESTING;
    this.description = task.description || '';
    this.created_at = task.created_at || new Date();
    this.updated_at = task.updated_at || new Date();
    this.address_child = task.address_child || '';
    this.is_available = task.is_available || false;
    this.is_global = task.is_global || false;
    this.is_owner = task.is_owner || false;
    this.global_group = task.global_group ? new Group(task.global_group) : null;
    this.expert_count = task.expert_count || 0;
    this.status = task.status;

    this.settings = new TaskSettings(task.settings || {}, 'task-settings');

    this.questions = (task.questions || [])
      .map((item) => new TaskQuestion(item, 'task-questions'));

    this.tournament_stage_group = new TournamentStageGroup(
      task.tournament_stage_group || {},
      'tournament-stage-group',
    );

    this.poll_journals = (task.poll_journals || [])
      .map((item) => new Journal(item, 'task-journal'));

    this.group = (this.is_global && this.global_group)
      ? this.global_group
      : this.tournament_stage_group.tournament_stage.tournament.event_group;

    this.nomination = (this.is_global && this.global_group)
      ? this.global_group.nomination
      : this.tournament_stage_group.tournament_stage.tournament.nomination;

    this.journals_count = task.journals_count || 0;
    this.event_name = task.event_name || '';
    this.nomination_name = task.nomination_name || '';
    this.group_name = task.group_name || '';
  }

  get displayName() {
    return this.name || 'Без названия';
  }

  get displayType() {
    const { type } = this;
    const typeData = this.constructor.glossary.TASK_TYPES_DATA[type];

    return {
      ...typeData,
      isAutomatedTesting: this.constructor.constants.TASK_TYPES.AUTOMATED_TESTING === type,
      isExpertReview: this.constructor.constants.TASK_TYPES.EXPERT_REVIEW === type,
    };
  }

  get displayQuestionsType() {
    const typeData = this.constructor.glossary.TASK_QUESTIONS_TYPES_DATA[this.constructor.constants.TASK_QUESTIONS_TYPES.MANUALLY];

    return {
      ...typeData,
      // isManually: constants.TASK_QUESTIONS_TYPES.MANUALLY === ,
      // isUploadingFile: constants.TASK_TYPES.EXPERT_REVIEW === type,
    };
  }

  get displayFormat() {
    const { is_online } = this.settings;
    const formatData = this.constructor.glossary.TASK_FORMATS_DATA[+is_online];

    return {
      ...formatData,
      isOnline: is_online,
      isOffline: !is_online,
    };
  }

  get displayCreator() {
    const { is_owner } = this;
    return this.constructor.glossary.TASK_CREATORS_DATA[+is_owner];
  }

  get displayStatus() {
    const { status } = this;
    const statusData = this.constructor.glossary.TASK_STATUSES_DATA[status];

    return {
      ...statusData,
      isDraft: this.constructor.constants.TASK_STATUSES.DRAFT === status,
      isPublished: this.constructor.constants.TASK_STATUSES.PUBLISHED === status,
      isDeleted: this.constructor.constants.TASK_STATUSES.DELETED === status,
    };
  }

  get displayAvailableDate() {
    const { settings, is_available } = this;
    const [availableStartAt, availableEndAt] = settings.availableDates;

    const isAvailableStartDateBeforeNow = (
      availableStartAt
      && isBefore(new Date(availableStartAt), new Date())
    );

    const isAvailableEndDateAfterNow = (
      availableEndAt
      && isAfter(new Date(availableEndAt), new Date())
    );

    if (is_available) {
      return {
        color: 'ui-success',
        text: [
          'Доступно',
          isAvailableEndDateAfterNow ? `до ${readableShortDateWithMonth(availableEndAt)}` : '',
        ].filter(Boolean).join(' '),
      };
    }

    if (!is_available && !isAvailableStartDateBeforeNow) {
      return {
        color: 'ui-warning',
        text: [
          'Станет доступно',
          !isAvailableStartDateBeforeNow ? `${readableShortDateWithMonth(availableStartAt)}` : '',
        ].filter(Boolean).join(' '),
      };
    }

    return {
      color: 'ui-error',
      text: [
        'Недоступно',
        !isAvailableEndDateAfterNow ? `c ${readableShortDateWithMonth(availableEndAt)}` : '',
      ].filter(Boolean).join(' '),
    };
  }

  get questionsCount() {
    return this.questions?.length || 0;
  }

  get lastPollJournal() {
    return this.poll_journals.at(-1);
  }

  get hasAttempts() {
    const { settings, lastPollJournal } = this;

    if (settings.attempts && lastPollJournal) {
      return settings.attempts > lastPollJournal.attempt;
    }

    return true;
  }

  get restAttempts() {
    const { settings, lastPollJournal } = this;
    return Math.max(0, settings.attempts - (lastPollJournal?.attempt || 0));
  }

  get isFinishedStatus() {
    return this.lastPollJournal?.isFinished;
  }

  get isPassingStatus() {
    return this.lastPollJournal && !this.lastPollJournal.isFinished;
  }

  get isWaitingStatus() {
    return !this.lastPollJournal;
  }

  static get constants() {
    return constants;
  }

  static get glossary() {
    return glossary;
  }

  static get functions() {
    return functions;
  }
}
